import { useMemo, useState } from "react";
import { History, Clock, type LucideIcon } from "lucide-react";
import { relativeAge } from "../../utils/relativeAge";
import { formatInstant } from "../../common/utilities/displayTime";
import { useDisplayTz } from "../../hooks/useDisplayTz";
import { useNavigate } from "react-router";
import {
  SkyBadge,
  SkyButton,
  SkyLoader,
  SkyPopover,
  type SkyBadgeColor,
} from "@skypointcloud/skypoint-ui";
import { useMsalAuth } from "../../hooks/useMsalAuth";
import { usePacks } from "../packs/PackContext";
import { useTerm } from "../../hooks/useTerm";
import {
  useGetRecentlyViewedQuery,
  type RecentlyViewedItem,
  type RecentsResourceType,
} from "../../store/api/crmRecentlyViewedApi";
import type { TermKey } from "../../../lib/packs/terminology";

/**
 * "Recently Viewed" chrome dropdown (access/activity audit log, Increment 1).
 *
 * A clock/history icon beside the NotificationBell that opens the CURRENT
 * user's ~10 most-recently-opened records (person / referrer person / resident
 * / episode), newest-first, each with a current name + terminology-mapped type
 * label, each navigating back to its detail surface. Self-scoped by
 * construction (the endpoint keys on the authenticated user; no user param).
 *
 * UI mirrors NotificationBell: strictly Sky* primitives on a SkyPopover, fetch
 * on open (no polling). Pack-gated identically — a CRM surface must not mount on
 * advisor-only instances (mounting = polling /crm/* = a fatal 403 loop), so the
 * inner component's query never runs there.
 */

// resource_type → (terminology key | null) + badge tint. Episode has no term
// key today, so it uses a static "Admission" label (per the ratified doc).
interface TypeVisual {
  termKey: TermKey | null;
  staticLabel?: string;
  color: SkyBadgeColor;
}
const TYPE_VISUAL: Record<RecentsResourceType, TypeVisual> = {
  person: { termKey: "prospect.singular", color: "informative" },
  referrer_person: { termKey: "referrer.singular", color: "brand" },
  resident: { termKey: "resident.singular", color: "success" },
  episode: { termKey: null, staticLabel: "Admission", color: "warning" },
};

// Relative "viewed x ago". The ladder is a DURATION and so tz-agnostic; past 7
// days it falls back to a date, and a date is an INSTANT — it must be rendered
// in the viewer's resolved display zone, not the browser's.
//
// This copy used `new Date(v).toLocaleDateString()`, which is browser-local.
// NotificationBell had the identical line and fixed it; this one never got the
// fix, because each surface owned a private copy of the same helper — exactly
// the cost `relativeAge` was extracted to stop. An item viewed at 2026-09-15
// 02:00Z read "9/14/2026" for an operator whose browser is UTC-7 while their
// community is Central, disagreeing with every other date in the product.
const timeAgo = (iso: string, zone: string): string =>
  relativeAge(iso, {
    beyond: { afterDays: 7, render: (v) => formatInstant(v, zone, { showTime: false }) },
  });

export function RecentlyViewedMenu() {
  const { hasOperatorPack } = usePacks();
  if (!hasOperatorPack) return null;
  return <RecentlyViewedMenuInner />;
}

function RecentlyViewedMenuInner() {
  const { getInstanceId, isLoggedIn } = useMsalAuth();
  // The zone the >7d date tail renders in — same resolution NotificationBell uses.
  const zone = useDisplayTz();
  const navigate = useNavigate();
  const term = useTerm();
  const [open, setOpen] = useState(false);

  const instanceId = isLoggedIn ? getInstanceId() || "" : "";

  const { data, isFetching } = useGetRecentlyViewedQuery(
    { instanceId },
    // Fetch only when the panel is open and we have an instance (no polling).
    // Refetch on each open so records viewed since the last open appear (the
    // list is inherently time-sensitive) rather than serving a stale cache.
    { skip: !open || !instanceId, refetchOnMountOrArgChange: true },
  );
  const items = useMemo(() => data?.items ?? [], [data]);

  const labelFor = (item: RecentlyViewedItem): string => {
    const v = TYPE_VISUAL[item.resource_type];
    return v.termKey ? term(v.termKey) : (v.staticLabel ?? item.resource_type);
  };

  const targetFor = (item: RecentlyViewedItem): string => {
    const id = item.resource_id;
    switch (item.resource_type) {
      case "person":
        return `/crm/persons/${id}/${instanceId}`;
      case "referrer_person":
        return `/crm/referrer-persons/${id}/${instanceId}`;
      case "resident":
        return `/crm/residents/${id}/${instanceId}`;
      case "episode":
        // No episode detail page exists yet — navigate to the admissions board.
        return `/crm/admissions/${instanceId}`;
    }
  };

  const openRecord = (item: RecentlyViewedItem) => {
    setOpen(false);
    navigate(targetFor(item));
  };

  if (!instanceId) return null;

  const trigger = (
    <SkyButton
      icon={History as LucideIcon}
      appearance="transparent"
      className="text-white! min-w-10! h-10!"
      aria-label="Recently viewed"
      onClick={() => setOpen((o) => !o)}
    />
  );

  return (
    <SkyPopover
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      placement="bottom-end"
    >
      <div className="w-[360px] max-w-[90vw]">
        <div className="flex items-center gap-2 border-b border-gray-100 px-3 pb-2.5 pt-0.5">
          <Clock
            size={16}
            className="text-gray-500"
            aria-hidden="true"
          />
          <span className="text-base font-semibold text-gray-900">Recently viewed</span>
        </div>

        <div className="max-h-[420px] overflow-y-auto px-1.5 py-1.5 flex flex-col gap-1">
          {isFetching ? (
            <div className="flex justify-center py-8">
              <SkyLoader label="Loading recently viewed" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <History
                className="h-8 w-8 text-gray-300"
                aria-hidden="true"
              />
              <span className="text-sm text-gray-500">Nothing viewed yet</span>
            </div>
          ) : (
            items.map((item) => (
              <button
                type="button"
                key={`${item.resource_type}:${item.resource_id}`}
                onClick={() => openRecord(item)}
                className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left"
              >
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm font-medium text-gray-900">
                    {item.display_name}
                  </span>
                  <span className="text-sm text-gray-400">
                    {timeAgo(item.last_viewed_at, zone)}
                  </span>
                </span>
                <SkyBadge
                  color={TYPE_VISUAL[item.resource_type].color}
                  size="small"
                >
                  {labelFor(item)}
                </SkyBadge>
              </button>
            ))
          )}
        </div>
      </div>
    </SkyPopover>
  );
}

export default RecentlyViewedMenu;
